import gql from "graphql-tag";
import { jwtDecode } from "jwt-decode";
import { BEFORE_SILENT_SIGN_IN_ATTEMPT, NO_SESSION, SESSION_EXPIRED, SIGNED_IN } from "~/constants/sessionStatuses";
import { SESSION_TOKEN } from "~/constants/settings";
import { getLogoutUrl } from "~/utils/user";
import { apolloClient } from "~/vue-apollo";

export const TOKEN_EXPIRY_SAFETY_MARGIN = 30000; // Renew the token before expiry for safety's sake
export const SESSION_EXPIRED_KEY = "SESSION_EXPIRED";

// The server rotates the refresh token on every silentSignIn, so a second
// concurrent call still carries the cookie the first one replaced and comes back
// InvalidRefreshToken — expiring a session that has in fact just been renewed.
// errorLink, attemptSilentSignIn and the scheduled timer can all ask for a
// renewal at the same moment, so they share one request.
let refreshInFlight = null;

const silentlySignIn = async (context) => {
  try {
    const result = await apolloClient.mutate({
      mutation: gql`
        mutation silentSignIn {
          silentSignIn {
            token
          }
        }
      `,
    });

    const { token } = result.data.silentSignIn;

    context.dispatch("handleTokenReceived", token);
  } catch (error) {
    if (error.graphQLErrors?.[0]?.extensions?.code === "invalid_refresh_token") {
      sessionStorage.setItem(SESSION_EXPIRED_KEY, "true");
      context.commit("updateSession", { status: SESSION_EXPIRED });
    } else if (error.graphQLErrors?.[0]?.extensions?.code === "unilogin_expired") {
      const currentUrl = encodeURIComponent(window.location.href);
      window.location.href = `/unilogin?return_to=${currentUrl}`;
    } else {
      context.commit("updateSession", { status: NO_SESSION });
    }
  }
};

const getDefaultState = () => ({
  status: BEFORE_SILENT_SIGN_IN_ATTEMPT,
  token: null,
  tokenData: null,
  refreshTimer: null,
});

export default {
  namespaced: true,
  state: getDefaultState(),

  getters: {
    status(state) {
      return state.status;
    },

    token(state) {
      return state.token;
    },

    tokenData(state) {
      return state.tokenData;
    },
  },

  mutations: {
    updateSession(state, newState) {
      Object.assign(state, newState);
    },
  },

  actions: {
    handleTokenReceived(context, token) {
      const newState = {
        status: SIGNED_IN,
        token,
        tokenData: jwtDecode(token),
      };

      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      localStorage.setItem(SESSION_TOKEN, token);
      context.dispatch("scheduleRefresh", newState.tokenData.exp);

      context.commit("updateSession", newState);
    },

    scheduleRefresh(context, expiry) {
      const timeToExpiry = new Date(expiry * 1000) - new Date();
      const delayWithSafetyMargin = timeToExpiry - TOKEN_EXPIRY_SAFETY_MARGIN;

      // Largest 32 bit signed integer:
      // https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout#Maximum_delay_value
      const maxDelay = 2147483647;
      const delay = Math.min(delayWithSafetyMargin, maxDelay);

      const refreshTimer = setTimeout(() => context.dispatch("refreshSession"), delay);

      context.commit("updateSession", { refreshTimer });
    },

    clearSession(context) {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      localStorage.removeItem(SESSION_TOKEN);

      clearTimeout(context.state.refreshTimer);

      context.commit("updateSession", {
        ...getDefaultState(),
        status: NO_SESSION,
      });
    },

    async signIn(context, { email, password }) {
      try {
        const result = await apolloClient.mutate({
          mutation: gql`
            mutation signIn($credentials: CredentialsAttributes!) {
              signIn(credentials: $credentials) {
                token
                otpRequired
                otpChallenge
              }
            }
          `,
          variables: { credentials: { email, password } },
        });

        const { token, otpRequired, otpChallenge } = result.data.signIn;

        // MFA: the password step issues no token — the caller must collect a
        // TOTP code and call `signInOtp` with the challenge (ENG-2882).
        if (otpRequired) return { otpRequired: true, otpChallenge };

        context.dispatch("handleTokenReceived", token);
        return { otpRequired: false };
      } catch (error) {
        if (error.graphQLErrors?.[0]?.extensions?.code === "invalid_credentials") {
          throw new Error("Invalid email or password", { cause: error });
        }

        // Re-throw anything else (e.g. `local_login_disabled` from
        // ENG-2765) so callers can react. Silently swallowing was a
        // long-standing bug — `LocalLogin.vue` relies on the ApolloError
        // shape to route the user.
        throw error;
      }
    },

    // Second step of an MFA login: redeem the challenge from `signIn` together
    // with a TOTP code, then store the issued token (ENG-2882).
    async signInOtp(context, { challenge, code }) {
      const result = await apolloClient.mutate({
        mutation: gql`
          mutation signInOtp($otpSignIn: OtpSignInAttributes!) {
            signInOtp(otpSignIn: $otpSignIn) {
              token
            }
          }
        `,
        variables: { otpSignIn: { challenge, code } },
      });

      context.dispatch("handleTokenReceived", result.data.signInOtp.token);
    },

    refreshSession(context) {
      refreshInFlight ||= silentlySignIn(context).finally(() => {
        refreshInFlight = null;
      });

      return refreshInFlight;
    },

    async signOut(context) {
      // Teardown must run whatever the mutation returns: the relogin modal signs
      // out in exactly the expired-session state the server rejects, and
      // rethrowing would strand the user on a page they cannot leave. Failures
      // that are not that case still reach Sentry through errorLink.
      try {
        await apolloClient.mutate({
          mutation: gql`
            mutation signOut {
              signOut {
                success
              }
            }
          `,
        });
      } catch {
        // Nothing to do here — see above.
      }

      const logoutUrl = getLogoutUrl();

      if (logoutUrl && typeof window !== "undefined") {
        window.location.href = logoutUrl;
      } else {
        context.dispatch("clearSession");
      }
    },

    attemptSilentSignIn({ state, dispatch, commit }) {
      if (state.status === BEFORE_SILENT_SIGN_IN_ATTEMPT) {
        if (sessionStorage.getItem(SESSION_EXPIRED_KEY)) {
          const token = localStorage.getItem(SESSION_TOKEN);

          if (token) {
            commit("updateSession", { status: SESSION_EXPIRED, tokenData: jwtDecode(token) });
            return Promise.resolve();
          }
        }

        return dispatch("refreshSession");
      }
      return Promise.resolve();
    },
  },
};
